import { FooterProps, TableColumn } from '../table/TableProps';
import { DataSortVO } from '@mc/data-filter-react';
import { FilterType } from '../filter/types';
import {
  FilterDef,
  filtersGetFieldValue,
  filtersIsFieldValueFilled,
} from '../filter/Filters';
import { ClientFilterDef } from './ClientTable';
import { parseISO } from 'date-fns';

/**
 * Porovná dva řetězce dle locale, podporuje předání prázdných proměnných ("", undefined, null).
 * @param oa
 * @param ob
 */
export const compareStrings = (oa?: string, ob?: string) => {
  if (oa == null && ob == null) {
    return 0;
  } else if (oa == null && ob != null) {
    return -1;
  } else if (oa != null && ob == null) {
    return 1;
  }

  // @ts-ignore
  let n = oa.localeCompare(ob, 'cs');
  if (n !== 0) {
    return n;
  } else {
    return n;
  }
};

export const getTableColumnCellValue = (column: TableColumn<any>, row: any) => {
  const par = column.accessor || column.id;
  if (!par) {
    return undefined;
  }

  return getDataRowColumnValue(row, par);
};
export const getTableFooterColumnCellValue = (
  footer: FooterProps<any>,
  footerData: any
): any | undefined => {
  const attr = footer?.accessor || footer.id;
  if (!attr) {
    return undefined;
  }
  return footerData[attr];
};

export const getTableRowValueByFilter = (filterDef: FilterDef, row: any) => {
  const par = filterDef.name;
  if (!par) {
    return undefined;
  }

  return getDataRowColumnValue(row, par);
};

export const getDataRowColumnValue = (row: any, attr?: string) => {
  if (!attr) {
    return undefined;
  }

  let value = attr.split('.').reduce((o, i) => (o ? o[i] : undefined), row);

  return value;
};
export const filterTableData = (
  rows: any[],
  filterType: FilterType,
  formFilter: any,
  filterDefs: ClientFilterDef[]
) => {
  if (!rows || !formFilter || !filterDefs || filterDefs.length === 0) {
    return rows;
  }

  return rows.filter((row) => {
    let keepRow = true;
    for (let a = 0; a < filterDefs.length; a++) {
      const fd = filterDefs[a];

      // Pokud není vyplněný filtr, jdeme dál - řádek pořád vyhovuje
      if (!filtersIsFieldValueFilled(filterType, formFilter, fd)) {
        continue;
      }

      const fieldValue = getTableRowValueByFilter(fd, row);

      // Filtr s hodnotou nevyplněno
      if (filterType === 'default' && formFilter[fd.name]?.emptyValue) {
        if (fieldValue != null) {
          keepRow = false;
          break;
        }
      }

      // Dle hodnoty
      const filterValue = filtersGetFieldValue(filterType, formFilter, fd);
      // Zde již víme, že je vyplněn nějaký filtr, ale pokud je hodnota ve sloupci prázdná, už víme, že nevyhovuje
      if (filterValue === undefined) {
        keepRow = false;
        break;
      }

      // Porovnání
      if (!testFilterValue(fieldValue, filterValue, fd)) {
        keepRow = false;
        break;
      }
    }

    return keepRow;
  });
};

const testFilterValue = (
  fieldValue: any,
  filterValue: any,
  filterDef: ClientFilterDef
): boolean => {
  const op = filterDef.operator || 'eq';
  const likeType = filterDef.likeType || 'contains';

  switch (filterDef.type) {
    case 'text':
      const fieldVal = fieldValue
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '');
      const filterVal = filterValue
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '');
      switch (op) {
        case 'eq':
          return fieldVal == filterVal;
        case 'like':
          switch (likeType) {
            case 'start':
              return fieldVal?.startsWith(filterVal);
            case 'end':
              return fieldVal?.endsWith(filterVal);
            case 'contains':
              return fieldVal.indexOf(filterVal) >= 0;
          }
          break;
      }
      break;
    case 'integerRange':
    case 'currencyRange':
    case 'currencyDecimalRange':
    case 'decimalRange':
      if (filterValue[0] != undefined && fieldValue < filterValue[0]) {
        return false;
      }
      if (filterValue[1] != undefined && fieldValue > filterValue[1]) {
        return false;
      }
      return true;
    case 'dateRange':
    case 'dateTimeRange':
      if (
        filterValue[0] != undefined &&
        +parseISO(fieldValue) < +parseISO(filterValue[0])
      ) {
        return false;
      }
      if (
        filterValue[1] != undefined &&
        +parseISO(fieldValue) > +parseISO(filterValue[1])
      ) {
        return false;
      }
      return true;
    case 'integer':
    case 'decimal':
    case 'currency':
      return filterValue == fieldValue;
    case 'date':
    case 'dateTime':
      return filterValue == fieldValue;
    case 'checkbox':
      return !!filterValue === !!fieldValue;
    case 'multiSelect':
    case 'selectBox':
      return filterValue == fieldValue;
    case 'apiMultiSelect':
      for (let a = 0; a < filterValue.length; a++) {
        if (filterValue[a]?.id == fieldValue?.id) {
          return true;
        }
      }
      return false;
    case 'autocomplete':
      return filterValue?.id == fieldValue?.id;
  }

  return false;
};

export const sortTableData = <D extends object = {}>(
  rows: any,
  columns: TableColumn<D>[],
  sort?: DataSortVO[]
) => {
  if (!rows || !sort || sort.length === 0) {
    return rows;
  }

  const map: { [key: string]: TableColumn<D> } = {};
  columns.forEach((column, columnIndex) => {
    const field = column.id || column.accessor || `column-${columnIndex}`;
    map[field] = column;
  });

  const result = [...rows];
  result.sort((a, b) => {
    for (let i = 0; i < sort.length; i++) {
      let s = sort[i];

      // No column/custom sort
      const column = map[s.field];
      if (!column) {
        continue;
      } else if (column.customSort) {
        let cellAValue = getTableColumnCellValue(column, a);
        let cellBValue = getTableColumnCellValue(column, b);

        // ---
        const n = column.customSort({
          rowA: a,
          rowB: b,
          valueA: cellAValue,
          valueB: cellBValue,
        });
        if (n !== 0) {
          return s.asc ? n : -1 * n;
        } else return n;
      } else if (!column.accessor && !column.id) {
        continue;
      }
      // ---

      const oa = getTableColumnCellValue(column, a);
      const ob = getTableColumnCellValue(column, b);

      if (oa == null && ob == null) {
        continue;
      } else if (oa == null && ob != null) {
        return s.asc ? -1 : 1;
      } else if (oa != null && ob == null) {
        return s.asc ? 1 : -1;
      }

      switch (column.type || 'text') {
        case 'text':
          {
            let n = oa.localeCompare(ob, 'cs');
            if (n !== 0) {
              return s.asc ? n : -1 * n;
            }
          }
          break;
        case 'integer':
        case 'decimal':
        case 'currency':
        case 'currencyDecimal':
          if (oa < ob) {
            return s.asc ? -1 : 1;
          } else if (oa > ob) {
            return s.asc ? 1 : -1;
          }
          break;
        case 'checkBox':
          break;
        case 'date':
        case 'dateTime': {
          let n = oa.localeCompare(ob, 'cs');
          return s.asc ? n : -1 * n;
          // let oad = parseISO(oa);
          // let obd = parseISO(ob);
          // if (+oad < +obd) {
          //   return s.asc ? -1 : 1;
          // } else if (+oad > +obd) {
          //   return s.asc ? 1 : -1;
          // }
        }
      }
    }
    return 0;
  });
  return result;
};
